import {
  type DeliveryView,
  DIGEST_HOURS,
  type MyNotifications,
  NOTIFICATION_CHANNELS,
  type NotificationSettingsInput,
  type NotificationSettingsView,
  type TelegramLinkCode,
  type TestSendResult,
  TIMEZONES,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { dateTime, plural } from '../lib/format';
import { Button } from '../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../ui/feedback';
import { Checkbox, Field, Select } from '../ui/form';
import { Panel } from '../ui/layout';

export const MY_NOTIFICATIONS_KEY = ['notifications', 'me'];

const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;

const STATUS: Record<DeliveryView['status'], { tone: 'success' | 'danger' | 'neutral'; title: string }> = {
  sent: { tone: 'success', title: 'доставлено' },
  failed: { tone: 'danger', title: 'ошибка' },
  skipped: { tone: 'neutral', title: 'пропущено' },
};

/** Строка журнала доставки — общая для профиля и администратора */
export function DeliveryList({ items, withName }: { items: DeliveryView[]; withName?: boolean }) {
  if (!items.length) return <p className="text-[14px] text-ink-2">Отправок пока не было.</p>;
  return (
    <ul className="flex flex-col divide-y divide-line text-[14px]">
      {items.map((d) => (
        <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
          <span className="num text-ink-2">{dateTime(d.sentAt)}</span>
          {withName ? <span className="font-medium text-ink">{d.userName ?? '—'}</span> : null}
          <span>{NOTIFICATION_CHANNELS[d.channel]}</span>
          <span className="text-ink-2">
            {d.kind === 'test'
              ? 'пробная'
              : d.kind === 'alert'
                ? 'оповещение'
                : d.kind === 'control'
                  ? 'контроль сроков'
                  : 'сводка'}
          </span>
          <Badge tone={STATUS[d.status].tone}>{STATUS[d.status].title}</Badge>
          {d.error ? <span className="text-danger [overflow-wrap:anywhere]">{d.error}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Уведомления в профиле (УВ-07, ЗД-06): утренняя сводка моих дел на почту и в Telegram, час сводки,
 * «Что нового» в сводке, подключение Telegram одноразовым кодом, пробная отправка и последние доставки.
 */
export function NotificationsPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: MY_NOTIFICATIONS_KEY,
    queryFn: () => api<MyNotifications>('/notifications/me'),
  });
  const [form, setForm] = useState<NotificationSettingsView | null>(null);
  const [link, setLink] = useState<TelegramLinkCode | null>(null);
  const [testResult, setTestResult] = useState<TestSendResult | null>(null);

  useEffect(() => {
    if (q.data && !form) setForm(q.data.settings);
  }, [q.data, form]);

  // Пока код действует, профиль раз в несколько секунд проверяет, не подключился ли Telegram; после срока — перестаёт
  const [codeExpired, setCodeExpired] = useState(false);
  useEffect(() => {
    if (!link) {
      setCodeExpired(false);
      return;
    }
    const t = setInterval(() => {
      if (new Date(link.expiresAt).getTime() < Date.now()) {
        setCodeExpired(true);
        return;
      }
      void qc.invalidateQueries({ queryKey: MY_NOTIFICATIONS_KEY });
    }, 4000);
    return () => clearInterval(t);
  }, [link, qc]);
  useEffect(() => {
    if (q.data?.telegram.linked && link) {
      setLink(null);
      // Сервер включил канал Telegram при привязке — форма берёт свежие настройки, чтобы «Сохранить» их не выключил
      setForm(q.data.settings);
      toast('Telegram подключён');
    }
  }, [q.data, link]);

  const save = useMutation({
    mutationFn: (body: NotificationSettingsInput) => api('/notifications/me', { method: 'PUT', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_NOTIFICATIONS_KEY });
      toast('Настройки уведомлений сохранены');
    },
  });
  const getCode = useMutation({
    mutationFn: () => api<TelegramLinkCode>('/notifications/me/telegram/link', { body: {} }),
    onSuccess: (x) => setLink(x),
  });
  const unlink = useMutation({
    mutationFn: () => api('/notifications/me/telegram', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_NOTIFICATIONS_KEY });
      toast('Telegram отключён');
    },
  });
  const test = useMutation({
    mutationFn: () => api<TestSendResult>('/notifications/me/test', { body: {} }),
    onSuccess: (x) => {
      setTestResult(x);
      void qc.invalidateQueries({ queryKey: MY_NOTIFICATIONS_KEY });
      const okCount = x.results.filter((r) => r.status === 'sent').length;
      toast(okCount ? 'Пробная сводка отправлена' : 'Отправить не удалось', okCount ? 'success' : 'danger');
    },
  });

  if (q.isPending || !form)
    return <Panel title="Уведомления">{q.isError ? <ErrorBox error={q.error} /> : <Spinner />}</Panel>;
  if (q.isError)
    return (
      <Panel title="Уведомления">
        <ErrorBox error={q.error} />
      </Panel>
    );
  const d = q.data;
  const dirty = JSON.stringify(form) !== JSON.stringify(d.settings);
  const set = (patch: Partial<NotificationSettingsView>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const tzTitle = (TIMEZONES as Record<string, string>)[d.timezone] ?? d.timezone;

  return (
    <Panel title="Уведомления" meta="утренняя сводка моих дел">
      <div className="flex flex-col gap-5 text-[14px]">
        <p className="max-w-prose text-ink-2">
          Каждое утро портал присылает одним письмом и одним сообщением, что ждёт вас: просроченное, срок
          сегодня и остальные дела из «Ждёт меня», а после выкладок — «Что нового». Суммы в сводку не
          включаются — они видны в портале.
        </p>

        <fieldset className="flex flex-col gap-3 [&>legend]:float-left [&>legend]:w-full">
          <legend className="mb-2 text-[13px] font-medium text-ink">Что присылать</legend>
          <Checkbox
            label="Утренняя сводка моих дел"
            checked={form.digestEnabled}
            onChange={(e) => set({ digestEnabled: e.target.checked })}
          />
          <Checkbox
            label="Обновления портала в сводке"
            description="Записи «Что нового» о разделах, которые вам открыты"
            checked={form.releasesEnabled}
            disabled={!form.digestEnabled}
            onChange={(e) => set({ releasesEnabled: e.target.checked })}
          />
          <Field
            label="Время сводки"
            hint={`По часам компании${d.companyName ? ` «${d.companyName}»` : ''}: ${tzTitle}`}
            className="max-w-xs"
          >
            <Select
              value={form.digestHour === null ? '' : String(form.digestHour)}
              disabled={!form.digestEnabled}
              onChange={(e) =>
                set({
                  digestHour:
                    e.target.value === '' ? null : (Number(e.target.value) as 6 | 7 | 8 | 9 | 10 | 11),
                })
              }
            >
              <option value="">Как у компании — {hourLabel(d.companyDigestHour)}</option>
              {DIGEST_HOURS.map((h) => (
                <option key={h} value={h}>
                  {hourLabel(h)}
                </option>
              ))}
              {form.digestHour !== null && !(DIGEST_HOURS as readonly number[]).includes(form.digestHour) ? (
                <option value={form.digestHour}>{hourLabel(form.digestHour)}</option>
              ) : null}
            </Select>
          </Field>
          <Checkbox
            label="Просрочки команды"
            description="Если вы руководитель проекта: отдельное письмо на почту раз в день, в час, заданный компанией, — просроченные поручения сотрудников вашей команды со ссылками"
            checked={form.controlEnabled ?? true}
            onChange={(e) => set({ controlEnabled: e.target.checked })}
          />
        </fieldset>

        <fieldset className="flex flex-col gap-3 border-t border-line pt-4 [&>legend]:float-left [&>legend]:w-full">
          <legend className="mb-2 text-[13px] font-medium text-ink">Куда присылать</legend>
          <Checkbox
            label={`Почта — ${d.email.address}`}
            description={
              d.email.configured ? undefined : 'Почтовый сервер ещё не настроен администратором портала'
            }
            checked={form.emailEnabled}
            onChange={(e) => set({ emailEnabled: e.target.checked })}
          />
          <Checkbox
            label={
              d.telegram.linked
                ? `Telegram — ${d.telegram.username ? `@${d.telegram.username}` : 'подключён'}`
                : 'Telegram — не подключён'
            }
            description={
              d.telegram.configured
                ? d.telegram.linkedAt
                  ? `Подключён ${dateTime(d.telegram.linkedAt)}`
                  : undefined
                : 'Бот Telegram ещё не настроен администратором портала'
            }
            checked={form.telegramEnabled}
            onChange={(e) => set({ telegramEnabled: e.target.checked })}
          />
          {d.telegram.configured ? (
            <div className="flex flex-col gap-3 pl-6">
              {d.telegram.linked ? (
                <div>
                  <Button variant="ghost" loading={unlink.isPending} onClick={() => unlink.mutate()}>
                    Отключить Telegram
                  </Button>
                </div>
              ) : link ? (
                <div className="flex flex-col gap-2 rounded-md border border-line bg-tile/40 p-3">
                  <p>
                    Откройте бота{' '}
                    {d.telegram.botUsername ? (
                      <span className="font-medium">@{d.telegram.botUsername}</span>
                    ) : (
                      'портала'
                    )}{' '}
                    и нажмите «Start», либо пришлите ему код:
                  </p>
                  <p className="num text-[22px] font-semibold tracking-wider text-ink">
                    <span className="sr-only">Код подключения: </span>
                    {link.code}
                  </p>
                  <p className={codeExpired ? 'text-[12px] text-danger' : 'text-[12px] text-ink-2'}>
                    {codeExpired
                      ? 'Код устарел — получите новый.'
                      : `Код действует до ${dateTime(link.expiresAt)} и подходит один раз. Как только бот примет код, здесь появится «подключён».`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {link.deepLink ? (
                      <a
                        href={link.deepLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-11 items-center rounded-md bg-ink px-4 font-medium text-surface hover:opacity-90 md:h-10"
                      >
                        Открыть бота в Telegram
                      </a>
                    ) : null}
                    <Button variant="ghost" onClick={() => setLink(null)}>
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Button loading={getCode.isPending} onClick={() => getCode.mutate()}>
                    Подключить Telegram
                  </Button>
                  <ErrorBox error={getCode.error} />
                </div>
              )}
            </div>
          ) : null}
        </fieldset>

        <ErrorBox error={save.error ?? unlink.error} />
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate(form as NotificationSettingsInput)}
          >
            Сохранить
          </Button>
          <Button
            loading={test.isPending}
            disabled={dirty || !(d.email.configured || d.telegram.configured)}
            onClick={() => test.mutate()}
            title={dirty ? 'Сначала сохраните настройки' : undefined}
          >
            Отправить пробную сводку
          </Button>
        </div>
        <ErrorBox error={test.error} />
        {testResult ? (
          <p className="text-ink-2">
            Пробная сводка ({plural(testResult.itemsCount, 'дело', 'дела', 'дел')}):{' '}
            {testResult.results
              .map(
                (r) =>
                  `${NOTIFICATION_CHANNELS[r.channel]} — ${STATUS[r.status].title}${r.error ? ` (${r.error})` : ''}`,
              )
              .join(', ')}
          </p>
        ) : null}

        <section className="border-t border-line pt-4">
          <h3 className="mb-2 text-[13px] font-medium text-ink">Последние отправки</h3>
          <DeliveryList items={d.deliveries} />
        </section>
      </div>
    </Panel>
  );
}
